import { adminService } from "@/api/admin/admin.service";
import type { TRoleResponse } from "@/api/admin/admin.types";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui";
import { extractErrorMessage } from "@/utils";
import { Loader2 } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";

type TAddAdminModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function AddAdminModal({ open, onOpenChange, onSuccess }: TAddAdminModalProps) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [roleName, setRoleName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<TRoleResponse[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // Fetch roles when modal opens
  useEffect(() => {
    if (open) {
      const fetchRoles = async () => {
        setLoadingRoles(true);
        setRolesError(null);
        const { data, error } = await adminService.getRoles();
        if (error) {
          const errorMessage = await extractErrorMessage(error);
          setRolesError(errorMessage);
        } else if (data?.data) {
          setRoles(data.data);
          // Set default role to first role if available
          if (data.data.length > 0 && !roleName) {
            setRoleName(data.data[0].name);
          }
        }
        setLoadingRoles(false);
      };
      void fetchRoles();
    }
  }, [open, roleName]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await adminService.promoteUser(email, roleName);

      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        setError(errorMessage);
      } else if (response.data) {
        toast({
          variant: "success",
          title: "Admin added",
          description: `${email} has been granted the ${roleName} role.`,
        });
        setEmail("");
        setRoleName("");
        onSuccess();
        onOpenChange(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to add admin";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail("");
      setRoleName("");
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>New Admin</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium"
              >
                Email address:
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address here"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="role"
                className="text-sm font-medium"
              >
                Account Role:
              </label>
              {loadingRoles ? (
                <div className="flex h-9 items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading roles...</span>
                </div>
              ) : rolesError ? (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">Failed to load roles: {rolesError}</div>
              ) : (
                <select
                  id="role"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                  disabled={loading || loadingRoles}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select an option</option>
                  {roles.map((role) => (
                    <option
                      key={role.role_id}
                      value={role.name}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
